import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import TabSkeleton, { TabSkeletonKind } from './TabSkeleton';

/**
 * Covers a tab with its own skeleton while the page builds, then lifts it.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT JUST FADE THE PAGE IN
 * ---------------------------------------------------------------------------
 * That was the first version, and it is what made the screen look blank: the
 * incoming page started at zero opacity and spent its whole animation faint.
 * The content is now drawn at full strength from the first frame, and the
 * skeleton sits *over* it - so nothing is ever dim, and what you see during the
 * gap is the shape of the page rather than nothing.
 *
 * `SlideIn` is wrong for tabs for a different reason: a push from the right
 * means "you have gone forward, and back will return you", which is true of the
 * cart and false of Home and Bookings. It also translates the page while it is
 * still measuring, which is the jump.
 *
 * ---------------------------------------------------------------------------
 * WHY A TIMER AND NOT A READINESS SIGNAL
 * ---------------------------------------------------------------------------
 * There is nothing to wait on. The pages are synchronous - no fetch, no promise
 * - so "ready" means "React has committed and the measured layouts have had a
 * frame", which no callback reports. `HOLD_MS` is a floor, not a guess at the
 * work: below about 150ms a cover reads as a flash, which is worse than the gap
 * it hides.
 */

/** How long the skeleton is held before it starts to lift. */
const HOLD_MS = 180;
/** How long it takes to lift. */
const LIFT_MS = 200;

type Props = {
  /**
   * Changes when the content does. The cover restarts on a new value and does
   * nothing on a re-render with the same one - otherwise every keystroke in a
   * search field would re-cover the page.
   */
  tabKey: string;
  /** Which layout the skeleton traces. Omitted for no cover at all. */
  skeleton?: TabSkeletonKind;
  children: ReactNode;
};

function TabFade({ tabKey, skeleton, children }: Props) {
  /** The cover's opacity. 1 while held, 0 once the page is up. */
  const cover = useRef(new Animated.Value(0)).current;
  const [covering, setCovering] = useState(false);

  useEffect(() => {
    if (!skeleton) {
      return;
    }
    setCovering(true);
    cover.setValue(1);

    const timer = setTimeout(() => {
      Animated.timing(cover, {
        toValue: 0,
        duration: LIFT_MS,
        useNativeDriver: true,
      }).start(({ finished }) => {
        /* Unmounted only once it is invisible. Dropping it on the first frame
           would put the skeleton's own animation back on the JS thread's plate
           at exactly the moment the page is committing. */
        if (finished) {
          setCovering(false);
        }
      });
    }, HOLD_MS);

    return () => clearTimeout(timer);
    // `cover` and `skeleton` are stable; the tab is what restarts this.
  }, [cover, skeleton, tabKey]);

  return (
    <View style={styles.fill}>
      {children}

      {covering && skeleton ? (
        <Animated.View
          style={[styles.cover, { opacity: cover }]}
          /* Never takes a tap. The page underneath is live from the first
             frame, so an early tap should reach it rather than be swallowed by
             something purely decorative. */
          pointerEvents="none"
        >
          <TabSkeleton kind={skeleton} />
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  /* Spelled out rather than `StyleSheet.absoluteFill`, which this React Native
     version does not expose on the style type. */
  cover: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});

export default TabFade;
